<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <div class="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" />
    </div>

    <!-- No analysis yet - need SERP analysis first -->
    <div v-else-if="!serpAnalysis" class="text-center py-8">
      <p class="text-gray-500 dark:text-gray-400">
        Complete SERP Analysis first to unlock competitor analysis.
      </p>
    </div>

    <!-- Step: Selection -->
    <CompetitorSelection
      v-else-if="currentStep === 'selection'"
      :serp-analysis="serpAnalysis"
      @start="handleStartAnalysis" />

    <!-- Step: Extracting / auto-verifying / analyzing progress -->
    <div v-else-if="currentStep === 'extracting' || currentStep === 'analyzing'" class="py-4">
      <div class="flex items-center gap-3 mb-3">
        <div class="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" />
        <span class="text-sm text-gray-600 dark:text-gray-400">{{ progressMessage }}</span>
      </div>
      <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div class="bg-primary-500 h-2 rounded-full transition-all duration-500" :style="{ width: progressWidth }" />
      </div>
    </div>

    <!-- Step: Results -->
    <CompetitorResults
      v-else-if="currentStep === 'results'"
      :competitors="competitors"
      :serp-analysis="serpAnalysis"
      @proceed-to-quality="currentStep = 'quality'" />

    <!-- Step: Quality Profile -->
    <CompetitorQualityProfile
      v-else-if="currentStep === 'quality'"
      :competitors="competitors"
      :benchmarks="serpAnalysis.benchmarks || []"
      :serp-analysis="serpAnalysis"
      :page-id="pageId"
      @approved="handleApproved"
      @back="currentStep = 'results'" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'
import { getCompetitorAnalysis, startCompetitorAnalysis, runForensicAudit, verifyCompetitor } from '~/javascript/api/competitor_analysis.js'
import CompetitorSelection from './competitor_selection.vue'
import CompetitorResults from './competitor_results.vue'
import CompetitorQualityProfile from './competitor_quality_profile.vue'

const props = defineProps({
  pageId: { type: String, required: true }
})

const emit = defineEmits(['quality-profile-approved'])

const serpAnalysis = ref(null)
const loading = ref(true)
const currentStep = ref('selection')
const progressMessage = ref('')
let subscription = null
let pollTimer = null
let autoVerifyTriggered = false

const competitors = computed(() => serpAnalysis.value?.competitor_pages || [])

const progressWidth = computed(() => {
  if (currentStep.value === 'extracting') return '33%'
  if (currentStep.value === 'analyzing') return '66%'
  return '0%'
})

const determineStep = () => {
  if (!serpAnalysis.value) { currentStep.value = 'selection'; return }
  const comps = competitors.value
  if (comps.length === 0) { currentStep.value = 'selection'; return }

  const allExtracted = comps.every((c) => c.extraction_status === 'extracted' || c.extraction_status === 'failed')
  const anyExtracting = comps.some((c) => c.extraction_status === 'extracting')
  const allVerified = comps.filter((c) => c.extraction_status === 'extracted').every((c) => ['verified', 'corrected'].includes(c.verification_status))
  const hasGrades = comps.some((c) => c.overall_quality_grade)

  if (anyExtracting || !allExtracted) { currentStep.value = 'extracting'; return }

  // Auto-verify extracted competitors and trigger forensic audit
  if (allExtracted && !allVerified && !autoVerifyTriggered) {
    autoVerifyTriggered = true
    autoVerifyAndAudit(comps)
    return
  }

  if (!allVerified || !hasGrades) {
    // Still processing (auto-verify/audit in progress)
    if (currentStep.value !== 'extracting' && currentStep.value !== 'analyzing') {
      currentStep.value = 'analyzing'
      progressMessage.value = 'Running forensic audit...'
    }
    return
  }

  // Check if benchmarks exist
  if (serpAnalysis.value.benchmarks && serpAnalysis.value.benchmarks.length > 0) {
    currentStep.value = 'quality'
  } else {
    currentStep.value = 'results'
  }
}

const autoVerifyAndAudit = async(comps) => {
  currentStep.value = 'analyzing'
  progressMessage.value = 'Auto-verifying competitor extractions...'

  try {
    // Verify all unverified extracted competitors
    const unverified = comps.filter((c) => c.extraction_status === 'extracted' && !['verified', 'corrected'].includes(c.verification_status))
    for (const comp of unverified) {
      await verifyCompetitor(comp.id, 'verified')
    }

    // Trigger forensic audit
    progressMessage.value = 'Running forensic audit...'
    await runForensicAudit(props.pageId)
    startPolling()
  } catch (e) {
    console.error('Auto-verify/audit failed:', e) // eslint-disable-line no-console
    // Fall back to polling to pick up state
    startPolling()
  }
}

const fetchAnalysis = async() => {
  try {
    loading.value = true
    const response = await getCompetitorAnalysis(props.pageId)
    serpAnalysis.value = response.data.serp_analysis
    determineStep()
  } catch (e) {
    console.error('Error fetching competitor analysis:', e) // eslint-disable-line no-console
  } finally {
    loading.value = false
  }
}

const subscribeToChannel = (serpAnalysisId) => {
  if (subscription) subscription.unsubscribe()
  subscription = consumer.subscriptions.create(
    { channel: 'CompetitorAnalysisChannel', serp_analysis_id: serpAnalysisId },
    {
      received(data) {
        progressMessage.value = data.message || ''
        if (data.step === 'awaiting_verification' || data.step === 'audit_complete') {
          stopPolling()
          fetchAnalysis()
        } else if (data.step === 'failed') {
          stopPolling()
          fetchAnalysis()
        }
      }
    }
  )
}

const handleStartAnalysis = async(selectedUrls) => {
  currentStep.value = 'extracting'
  progressMessage.value = 'Starting competitor extraction...'
  autoVerifyTriggered = false
  try {
    await startCompetitorAnalysis(props.pageId, selectedUrls)
    if (serpAnalysis.value?.id) {
      subscribeToChannel(serpAnalysis.value.id)
    }
    startPolling()
  } catch (e) {
    console.error('Failed to start analysis:', e) // eslint-disable-line no-console
    currentStep.value = 'selection'
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const response = await getCompetitorAnalysis(props.pageId)
      serpAnalysis.value = response.data.serp_analysis
      determineStep()
      const step = currentStep.value
      if (step !== 'extracting' && step !== 'analyzing') {
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const handleApproved = () => {
  fetchAnalysis()
  emit('quality-profile-approved')
}

onMounted(async() => {
  await fetchAnalysis()
  if (serpAnalysis.value?.id) {
    subscribeToChannel(serpAnalysis.value.id)
  }
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
